// Mundo Maximum Dosage VFX — pulsing green healing aura with rising particles over 3 seconds.

import type { Animation } from "../../engine/animation/AnimationQueue";
import type { Point } from "../../engine/hex/HexLayout";

const DURATION = 3000;
const PARTICLE_COUNT = 8;

interface Particle {
  angle: number;
  speed: number;
  size: number;
  phase: number;
}

export class MundoUltVFX implements Animation {
  private elapsed = 0;
  private particles: Particle[];

  constructor(
    private center: Point,
    private radius: number,
  ) {
    this.particles = Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
      angle: (i / PARTICLE_COUNT) * Math.PI * 2,
      speed: 0.6 + Math.random() * 0.8,
      size: 2.5 + Math.random() * 2.5,
      phase: Math.random() * Math.PI * 2,
    }));
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, DURATION);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const t = this.elapsed / DURATION;
    const fade = t < 0.8 ? 1 : 1 - (t - 0.8) / 0.2;
    const pulse = 0.5 + 0.5 * Math.sin(t * Math.PI * 6);

    ctx.save();

    // Outer glow ring
    ctx.globalAlpha = fade * 0.55 * (0.7 + 0.3 * pulse);
    ctx.strokeStyle = "#22c55e";
    ctx.lineWidth = 4;
    ctx.shadowColor = "#16a34a";
    ctx.shadowBlur = 22;
    ctx.beginPath();
    ctx.arc(this.center.x, this.center.y, this.radius * (0.9 + 0.12 * pulse), 0, Math.PI * 2);
    ctx.stroke();

    // Inner fill
    ctx.globalAlpha = fade * 0.12 * (0.6 + 0.4 * pulse);
    ctx.fillStyle = "#4ade80";
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.arc(this.center.x, this.center.y, this.radius * 0.85, 0, Math.PI * 2);
    ctx.fill();

    // Rising heal particles
    for (const p of this.particles) {
      const localT = ((t * DURATION + p.phase * 500) % 1000) / 1000;
      const rise = localT;
      const px = this.center.x + Math.cos(p.angle + t * p.speed) * this.radius * 0.65;
      const py = this.center.y - rise * this.radius * 1.4;
      const alpha = fade * Math.sin(localT * Math.PI) * 0.85;

      ctx.globalAlpha = alpha;
      ctx.fillStyle = "#86efac";
      ctx.shadowColor = "#22c55e";
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(px, py, p.size * (1 - rise * 0.4), 0, Math.PI * 2);
      ctx.fill();
    }

    // Plus-sign cross flash on each tick (at 0ms, 1000ms, 2000ms)
    for (let tick = 0; tick < 3; tick++) {
      const tickT = (t * DURATION - tick * 1000) / 300;
      if (tickT > 0 && tickT < 1) {
        const flashFade = Math.sin(tickT * Math.PI);
        const arm = this.radius * 0.45;
        ctx.globalAlpha = flashFade * fade * 0.9;
        ctx.strokeStyle = "#bbf7d0";
        ctx.lineWidth = 4;
        ctx.shadowColor = "#22c55e";
        ctx.shadowBlur = 14;
        ctx.beginPath();
        ctx.moveTo(this.center.x - arm, this.center.y);
        ctx.lineTo(this.center.x + arm, this.center.y);
        ctx.moveTo(this.center.x, this.center.y - arm);
        ctx.lineTo(this.center.x, this.center.y + arm);
        ctx.stroke();
      }
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= DURATION;
  }
}
